import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { io, Socket } from 'socket.io-client';
import { Clock, Calendar, User, LogIn, LogOut, Filter, Download } from 'lucide-react';
import type { User as UserType } from '../types';

interface LoginHistoryRecord {
  id: string;
  agentId: string;
  loginAt: string;
  logoutAt: string | null;
  createdAt: string;
  agent: {
    id: string;
    name: string;
    email: string;
    role: string;
    status: string;
    department: {
      id: string;
      name: string;
    } | null;
  };
}

export const UserLoginReport: React.FC = () => {
  const { isAdmin, agent } = useAuth();
  const [history, setHistory] = useState<LoginHistoryRecord[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const socketRef = useRef<Socket | null>(null);

  // Verificar se o usuário tem permissão para acessar esta página
  const hasPermission = isAdmin() || agent?.role === 'GENERAL_MANAGER';

  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-4">Acesso negado</p>
          <p className="text-gray-500 dark:text-gray-500">
            Você não tem permissão para acessar este relatório.
          </p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    loadHistory();
  }, [selectedUser, selectedPeriod, startDate, endDate, useCustomRange]);

  // WebSocket listener for real-time login/logout updates
  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
    console.log('🔌 [LoginReport] Initializing WebSocket for login history updates');

    const socket = io(apiUrl, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ [LoginReport] WebSocket connected:', socket.id);
      setIsWebSocketConnected(true);
    });

    socket.on('agent-login-history-updated', (data: any) => {
      console.log('📊 [LoginReport] Login history updated:', data);

      // Update status immediately if provided in the event
      if (data.agentStatus) {
        setHistory((prevHistory) =>
          prevHistory.map((record) =>
            record.agent.id === data.agentId
              ? { ...record, agent: { ...record.agent, status: data.agentStatus } }
              : record
          )
        );
      }

      // Reload history to get the new login/logout record
      loadHistory();
    });

    socket.on('agent-status-updated', (data: { agentId: string; status: string }) => {
      console.log('📊 [LoginReport] Agent status updated:', data);
      // Update agent status in history records
      setHistory((prevHistory) =>
        prevHistory.map((record) =>
          record.agent.id === data.agentId
            ? { ...record, agent: { ...record.agent, status: data.status } }
            : record
        )
      );
    });

    socket.on('disconnect', () => {
      console.log('❌ [LoginReport] WebSocket disconnected');
      setIsWebSocketConnected(false);
    });

    socket.on('error', (error: any) => {
      console.error('❌ [LoginReport] WebSocket error:', error);
    });

    // Cleanup on unmount
    return () => {
      console.log('🔌 [LoginReport] Cleaning up WebSocket connection');
      socket.off('agent-login-history-updated');
      socket.off('agent-status-updated');
      socket.disconnect();
    };
  }, [selectedUser, selectedPeriod, startDate, endDate, useCustomRange]);

  const loadUsers = async () => {
    try {
      // Admins e gerentes gerais podem ver todos os usuários
      const usersData = await api.getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const params: any = {};

      if (selectedUser !== 'all') {
        params.agentId = selectedUser;
      }

      if (useCustomRange && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (!useCustomRange) {
        params.period = selectedPeriod;
      }

      const historyData = await api.getLoginHistory(params);
      setHistory(historyData);
    } catch (error) {
      console.error('Failed to load login history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const calculateDuration = (loginAt: string, logoutAt: string | null) => {
    const login = new Date(loginAt);
    const logout = logoutAt ? new Date(logoutAt) : new Date();
    const diff = logout.getTime() - login.getTime();

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  };

  const exportToCSV = () => {
    const headers = ['Usuário', 'Email', 'Departamento', 'Login', 'Logout', 'Duração', 'Status Atual', 'Status Sessão'];
    const rows = history.map(record => [
      record.agent.name,
      record.agent.email,
      record.agent.department?.name || 'Sem departamento',
      formatDateTime(record.loginAt),
      record.logoutAt ? formatDateTime(record.logoutAt) : '-',
      calculateDuration(record.loginAt, record.logoutAt),
      record.agent.status,
      record.logoutAt ? 'Encerrado' : 'Sessão Ativa',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio-login-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      ADMIN: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      GENERAL_MANAGER: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
      DEPARTMENT_MANAGER: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      AGENT: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    };

    const labels = {
      ADMIN: 'Admin',
      GENERAL_MANAGER: 'Gerente Geral',
      DEPARTMENT_MANAGER: 'Gerente',
      AGENT: 'Agente',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role as keyof typeof colors]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    );
  };

  const getAgentStatusBadge = (status: string) => {
    const configs = {
      ONLINE: {
        color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
        label: 'Online',
        dotColor: 'bg-green-600 dark:bg-green-400',
      },
      OFFLINE: {
        color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
        label: 'Offline',
        dotColor: 'bg-gray-600 dark:bg-gray-400',
      },
      OCUPADO: {
        color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
        label: 'Ocupado',
        dotColor: 'bg-red-600 dark:bg-red-400',
      },
      AUSENTE: {
        color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
        label: 'Ausente',
        dotColor: 'bg-yellow-600 dark:bg-yellow-400',
      },
      EM_REUNIAO: {
        color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
        label: 'Em Reunião',
        dotColor: 'bg-blue-600 dark:bg-blue-400',
      },
    };

    const config = configs[status as keyof typeof configs] || configs.OFFLINE;

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color} flex items-center space-x-1 w-fit`}>
        <span className={`w-2 h-2 ${config.dotColor} rounded-full`}></span>
        <span>{config.label}</span>
      </span>
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(history.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedHistory = history.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-3">
            <span>Relatório de Login/Logout</span>
            {isWebSocketConnected && (
              <span className="flex items-center space-x-2 text-sm font-normal text-green-600 dark:text-green-400">
                <span className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full animate-pulse"></span>
                <span>Atualização em tempo real</span>
              </span>
            )}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Histórico de acessos dos usuários ao sistema
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded-lg transition flex items-center space-x-2"
        >
          <Download className="w-4 h-4" />
          <span>Exportar CSV</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* User Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Usuário
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="all">Todos os usuários</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} - {user.email}
                </option>
              ))}
            </select>
          </div>

          {/* Period Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Filtro
            </label>
            <select
              value={useCustomRange ? 'custom' : 'period'}
              onChange={(e) => setUseCustomRange(e.target.value === 'custom')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="period">Período Rápido</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>

          {/* Period or Custom Range */}
          {!useCustomRange ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Período
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as 'day' | 'week' | 'month')}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="day">Hoje</option>
                <option value="week">Esta Semana</option>
                <option value="month">Este Mês</option>
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total de Sessões</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{history.length}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Sessões Ativas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {history.filter(h => !h.logoutAt).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <User className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Sessões Encerradas</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {history.filter(h => h.logoutAt).length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <LogOut className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Departamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Logout
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Duração
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status Atual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status Sessão
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum registro encontrado para os filtros selecionados</p>
                  </td>
                </tr>
              ) : (
                paginatedHistory.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {record.agent.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {record.agent.email}
                        </div>
                        <div className="mt-1">
                          {getRoleBadge(record.agent.role)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {record.agent.department?.name || 'Sem departamento'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900 dark:text-white">
                        <LogIn className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span>{formatDateTime(record.loginAt)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.logoutAt ? (
                        <div className="flex items-center space-x-2 text-sm text-gray-900 dark:text-white">
                          <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
                          <span>{formatDateTime(record.logoutAt)}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400 italic">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span>{calculateDuration(record.loginAt, record.logoutAt)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getAgentStatusBadge(record.agent.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.logoutAt ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          Encerrado
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 flex items-center space-x-1 w-fit">
                          <span className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full animate-pulse"></span>
                          <span>Sessão Ativa</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {history.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Mostrando {startIndex + 1} a {Math.min(endIndex, history.length)} de {history.length} registros
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                translate="no"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300" translate="no">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                translate="no"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
